import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Loader2,
  Play,
  Activity,
  CheckCircle2,
  AlertCircle,
  Clock,
  X,
  Trash2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { IntegrationEmptyState } from "@/components/IntegrationEmptyState";

const WRITE_ROLES = ["owner", "admin", "researcher"];

const RUN_STATUS = {
  queued: { Icon: Clock, cls: "border-slate-200 bg-slate-50 text-slate-700" },
  running: { Icon: Activity, cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  completed: { Icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  failed: { Icon: AlertCircle, cls: "border-rose-200 bg-rose-50 text-rose-700" },
  cancelled: { Icon: X, cls: "border-slate-200 bg-slate-50 text-slate-500" },
};

function RunRow({ run, onClick }) {
  const { t } = useTranslation("projects");
  const cfg = RUN_STATUS[run.status] || RUN_STATUS.queued;
  const Icon = cfg.Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`run-row-${run.id}`}
      className="flex w-full items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:bg-slate-50"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cfg.cls}>
            <Icon className="mr-1 h-3 w-3" strokeWidth={2} />
            {t(`list.status.${run.status}`)}
          </Badge>
          <span className="text-[11px] text-slate-500">
            {run.created_at && new Date(run.created_at).toLocaleString()}
          </span>
        </div>
        {run.error?.detail && (
          <p className="mt-1 text-xs text-rose-700">{run.error.detail}</p>
        )}
        {run.status === "completed" && (
          <p className="mt-1 text-xs text-slate-500">
            {Object.entries(run.source_counts || {})
              .map(([k, v]) => `${v} ${k.replace("_", " ")}`)
              .join(" · ") || "—"}
          </p>
        )}
      </div>
      <ArrowLeft className="h-4 w-4 rotate-180 text-slate-400" strokeWidth={1.75} />
    </button>
  );
}

export default function ProjectDetail() {
  const { t } = useTranslation(["projects", "common", "errors"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [project, setProject] = useState(null);
  const [brand, setBrand] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [startError, setStartError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/research/projects/${id}`);
      setProject(data);
      const [bRes, rRes] = await Promise.all([
        api.get(`/brands/${data.brand_id}`),
        api.get(`/research/projects/${id}/runs`),
      ]);
      setBrand(bRes.data);
      setRuns(rRes.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(t("errors:notFound"));
        navigate("/projects");
        return;
      }
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => { load(); }, [load]);

  const startRun = async () => {
    setStarting(true);
    setStartError("");
    try {
      const { data } = await api.post(`/research/projects/${id}/runs`);
      navigate(`/projects/${id}/runs/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && /not_configured/.test(detail)) {
        setStartError(detail);
      } else if (detail === "llm_failed") {
        toast.error(t("errors:llm_failed.title"));
      } else {
        toast.error(formatApiError(err, t("errors:generic")));
      }
    } finally {
      setStarting(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/research/projects/${id}`);
      toast.success(t("projects:detail.deleted"));
      navigate("/projects");
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading || !project) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="project-detail-page">
      <Link
        to="/projects"
        data-testid="project-detail-back"
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> {t("projects:detail.backToList")}
      </Link>

      <header className="space-y-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <Sparkles className="mr-1 inline h-3 w-3" strokeWidth={2} /> {t(`projects:types.${project.type}`)}
        </span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
          {project.name}
        </h1>
        {brand && (
          <Link to={`/brands/${brand.id}`} className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-600">
            {brand.name}
          </Link>
        )}
      </header>

      {/* Action row */}
      {canWrite && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={startRun}
            disabled={starting}
            data-testid="project-start-run"
            className="bg-slate-900 text-white hover:bg-slate-800"
          >
            {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <>
                <Play className="mr-1 h-4 w-4" strokeWidth={2} />
                {t("projects:detail.newRun")}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            data-testid="project-delete"
            className="border-rose-200 text-rose-700 hover:bg-rose-50"
          >
            <Trash2 className="mr-1 h-4 w-4" strokeWidth={1.75} /> {t("projects:detail.delete")}
          </Button>
        </div>
      )}

      {startError === "firecrawl_not_configured" && (
        <IntegrationEmptyState integration="firecrawl" />
      )}
      {startError === "tavily_not_configured" && (
        <IntegrationEmptyState integration="tavily" />
      )}

      {/* Project meta card */}
      <div className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 md:p-8 sm:grid-cols-2">
        <MetaRow label={t("projects:detail.fields.brand")} value={brand?.name || "—"} />
        <MetaRow label={t("projects:detail.fields.type")} value={t(`projects:types.${project.type}`)} />
        <MetaRow label={t("projects:detail.fields.language")} value={project.language?.toUpperCase()} />
        <MetaRow label={t("projects:detail.fields.country")} value={project.country || "—"} />
        <MetaRow label={t("projects:detail.fields.depth")} value={t(`projects:depth.${project.depth}`)} />
        <MetaRow
          label={t("projects:detail.fields.sourceTypes")}
          value={(project.source_types || []).map((s) => t(`projects:sourceTypes.${s}`)).join(" · ")}
        />
        <MetaRow
          label={t("projects:detail.fields.competitors")}
          value={String((project.competitor_ids || []).length)}
        />
        <MetaRow
          label={t("projects:detail.fields.customQueries")}
          value={String((project.custom_queries || []).length)}
        />
      </div>

      {/* Runs list */}
      <section className="space-y-3" data-testid="project-runs-section">
        <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">
          {t("projects:detail.runs.title")}
        </h2>
        {runs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500" data-testid="project-runs-empty">
            {t("projects:detail.runs.empty")}
          </div>
        ) : (
          <div className="space-y-2">
            {runs.map((r) => (
              <RunRow key={r.id} run={r} onClick={() => navigate(`/projects/${id}/runs/${r.id}`)} />
            ))}
          </div>
        )}
      </section>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="project-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("projects:detail.delete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("projects:detail.confirmDelete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="project-confirm-delete-cancel">{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} disabled={deleting} data-testid="project-confirm-delete-confirm" className="bg-rose-600 text-white hover:bg-rose-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("projects:detail.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-sm text-slate-900">{value || "—"}</p>
    </div>
  );
}
